import { Year } from "@/features/pe_aldd";
import { Rcp } from "@/types/rcp";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { CompareResultsMode } from "../../types";
import { ChartDataPoint } from "./common/types";
import { formatMetricValue, MetricKey, PERCENT_METRICS } from "./common/utils";
interface ScenarioMiniRangeGridProps {
  chartData: ChartDataPoint[];
  metric: MetricKey;
  scenarios: Rcp[];
  rcp: Rcp;
  year: Year;
  yAxisUnit: "%" | "$";
  scenarioColors: Record<Rcp, string>;
  formatValue: (val: number) => string;
  onChangeRcp?: (rcp: Rcp) => void;
  compareResultsMode?: CompareResultsMode;
}
export const ScenarioMiniRangeGrid = ({
  chartData,
  metric,
  scenarios,
  rcp,
  year,
  scenarioColors,
  formatValue,
  onChangeRcp,
  compareResultsMode
}: ScenarioMiniRangeGridProps) => {
  const {
    t
  } = useTranslation("terms");
  if (!chartData.length || !scenarios.length) {
    return <div className="flex h-full items-center justify-center text-gray-400">Scenario data not available</div>;
  }
  const stats = scenarios.slice().sort((a, b) => a.localeCompare(b)).map(scenario => {
    const values = chartData.map(d => typeof d[scenario] === "number" ? d[scenario] as number : undefined).filter((v): v is number => typeof v === "number");
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 0;
    const currentPoint = chartData.find(d => d.year === year);
    const current = currentPoint && typeof currentPoint[scenario] === "number" ? currentPoint[scenario] as number : 0;
    const label = t(`scenarios.${scenario}.short`);
    return {
      scenario,
      min,
      max,
      current,
      label
    };
  });
  const globalMin = Math.min(...stats.map(s => s.min));
  const globalMax = Math.max(...stats.map(s => s.max));
  const denom = globalMax - globalMin || 1;
  const toPercent = (val: number) => Math.min(100, Math.max(0, (val - globalMin) / denom * 100));
  return <div className={`grid ${compareResultsMode === "compare" ? "grid-cols-2" : "grid-cols-2"} gap-2 @sm/sub:grid-cols-2 @xl/sub:grid-cols-4 @2xl/sub:grid-cols-2 @3xl/sub:grid-cols-4 @4xl/sub:mt-0 @4xl/sub:grid-flow-row`} data-sentry-component="ScenarioMiniRangeGrid" data-sentry-source-file="ScenarioMiniRangeGrid.tsx">
      {stats.reverse().map(({
      scenario,
      min,
      max,
      current,
      label
    }) => {
      const isSelected = scenario === rcp;
      const left = toPercent(min);
      const right = toPercent(max);
      const width = Math.max(0, right - left);
      const marker = toPercent(current);
      const color = scenarioColors[scenario];
      const title = `${scenario.toUpperCase()} @ ${year}: ${formatValue(current)}`;
      const isPercent = PERCENT_METRICS.includes(metric);
      const currentValue = formatMetricValue(isPercent ? "%" : "$", current, isPercent ? 4 : 2);
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <div key={scenario} className={classNames(`cursor-pointer rounded-xl border p-3 transition-all active:scale-[0.995] active:border-[var(--active-border-color)] active:bg-[var(--active-bg-color)]`, {
          "opacity-50": !isSelected,
          "border-[var(--active-border-color)]": isSelected,
          "bg-[var(--active-bg-color)]": isSelected,
          "hover:border-gray-400 hover:bg-gray-50 hover:!opacity-100": !isSelected
        })} style={{
          "--active-border-color": color + "50",
          "--active-bg-color": color + "10"
        } as React.CSSProperties} title={title} role="button" tabIndex={0} onClick={() => onChangeRcp?.(scenario)}>
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-1 text-sm">
                <div className="h-3 w-3 rounded-sm" style={{
                backgroundColor: color
              }} />
                {label}
              </div>
              <div className="text-gray-500">{year}</div>
            </div>
            <div className="mb-2 text-base font-semibold tabular-nums tracking-tight text-gray-900">{currentValue}</div>
            <div className="relative h-1 w-full rounded-full bg-gray-200">
              <div className="absolute top-0 h-1 rounded-full bg-gray-400" style={{
              left: `${left}%`,
              width: `${width}%`
            }} />
              <div className={`absolute -top-[6px] h-4 w-2 rounded-full shadow-sm`} style={{
              left: `calc(${marker}% - 4px)`,
              backgroundColor: color
            }} />
            </div>
            <div className="relative mt-2 w-full overflow-hidden text-xs tabular-nums text-gray-500">
              {right <= 30 ? <span>
                  {formatValue(min)} - {formatValue(max)}
                </span> : <>
                  <span>{formatValue(min)}</span>
                  <span className="absolute" style={{
                left: `${right}%`,
                transform: right >= 75 ? "translateX(-100%)" : right >= 50 ? "translateX(-50%)" : "translateX(-25%)"
              }}>
                    {formatValue(max)}
                  </span>
                </>}
            </div>
          </div>
      );
    })}
    </div>;
};
export default ScenarioMiniRangeGrid;